import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Download,
  TrendingUp,
  TrendingDown,
  LocalShipping,
  CheckCircle,
  Schedule,
  FlightTakeoff,
  Analytics as AnalyticsIcon,
  Refresh,
  DateRange
} from '@mui/icons-material';
import { ProjectContext } from '../contexts/ProjectContext';
import analyticsService from '../services/analyticsService';
import MetricsOverview from '../components/analytics/MetricsOverview';
import TrendsChart from '../components/analytics/TrendsChart';
import GeographicChart from '../components/analytics/GeographicChart';
import ProjectComparison from '../components/analytics/ProjectComparison';
import MonthlyReport from '../components/analytics/MonthlyReport';

const AnalyticsDashboard = () => {
  const { selectedProject } = useContext(ProjectContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [refreshing, setRefreshing] = useState(false);
  
  // Analytics data state
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [geographic, setGeographic] = useState([]);
  const [projectComparison, setProjectComparison] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState(null);

  const periods = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' }
  ];

  const fetchAnalyticsData = async () => {
    if (!selectedProject) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [overviewData, trendsData, geoData, comparisonData, monthlyData] = await Promise.all([
        analyticsService.getOverview(selectedProject.id, period),
        analyticsService.getTrends(selectedProject.id, period),
        analyticsService.getGeographicData(selectedProject.id),
        analyticsService.getProjectComparison(period),
        analyticsService.getMonthlyReport(selectedProject.id)
      ]);

      setOverview(overviewData);
      setTrends(trendsData);
      setGeographic(geoData);
      setProjectComparison(comparisonData);
      setMonthlyReport(monthlyData);
    } catch (err) {
      setError('Failed to load analytics data. Please try again.');
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalyticsData();
    setRefreshing(false);
  };

  const handleExportCSV = async () => {
    if (!selectedProject) return;
    
    try {
      await analyticsService.exportCSV(selectedProject.id, period);
    } catch (err) {
      setError('Failed to export data. Please try again.');
      console.error('Export error:', err);
    }
  };

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedProject, period]);

  if (!selectedProject) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        flexDirection: 'column'
      }}>
        <AnalyticsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please select a project to view analytics
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon sx={{ fontSize: 32 }} />
            Analytics Dashboard
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={period}
                onChange={handlePeriodChange}
                label="Time Period"
                startAdornment={<DateRange sx={{ mr: 1, color: 'text.secondary' }} />}
              >
                {periods.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                {refreshing ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
              </IconButton>
            </Tooltip>
            
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExportCSV}
              sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip 
            label={selectedProject.name}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 'bold' }}
          />
          <Typography variant="body2" color="text.secondary">
            Analytics for {periods.find(p => p.value === period)?.label}
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Metrics Overview */}
          <Grid item xs={12}>
            <MetricsOverview data={overview} />
          </Grid>

          {/* Trends Chart */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp />
                  Shipment Trends
                </Typography>
                <TrendsChart data={trends} />
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Stats
                </Typography>
                {overview && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalShipping color="primary" />
                        <Typography variant="body2">Total Shipments</Typography>
                      </Box>
                      <Typography variant="h6" color="primary">
                        {overview.summary.total_shipments.toLocaleString()}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle color="success" />
                        <Typography variant="body2">Delivered</Typography>
                      </Box>
                      <Typography variant="h6" color="success.main">
                        {overview.summary.delivered_shipments.toLocaleString()}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule color="warning" />
                        <Typography variant="body2">Pending</Typography>
                      </Box>
                      <Typography variant="h6" color="warning.main">
                        {overview.summary.pending_shipments.toLocaleString()}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FlightTakeoff color="info" />
                        <Typography variant="body2">Avg Delivery Time</Typography>
                      </Box>
                      <Typography variant="h6" color="info.main">
                        {overview.summary.avg_delivery_days || 0} days
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Delivery Rate
                      </Typography>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                        {overview.summary.delivery_rate}%
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {overview.summary.delivery_rate >= 80 ? (
                          <TrendingUp color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                        ) : (
                          <TrendingDown color="error" sx={{ fontSize: 16, mr: 0.5 }} />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {overview.summary.delivery_rate >= 80 ? 'Excellent' : 'Needs Improvement'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Geographic Distribution */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Geographic Distribution
                </Typography>
                <GeographicChart data={geographic} />
              </CardContent>
            </Card>
          </Grid>

          {/* Project Comparison */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Comparison
                </Typography>
                <ProjectComparison data={projectComparison} />
              </CardContent>
            </Card>
          </Grid>

          {/* Monthly Report */}
          <Grid item xs={12}>
            <MonthlyReport data={monthlyReport} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AnalyticsDashboard;